import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { LuCheck, LuEye, LuSend, LuX } from "react-icons/lu";
import { formatCurrencyAUD } from "utils/formatCurrency";
import QuotationRecipientField from "./QuotationRecipientField";

export default function QuotationSendDialog({
  id,
  values,
  organisation,
  currencyCode,
  recipient,
  recipientError,
  additionalRecipientsError,
  onRecipientsChange,
  onRecipientsError,
  subject,
  onSubjectChange,
  attachPdf,
  onAttachPdfChange,
  sendCopy,
  onSendCopyChange,
  busy,
  sendingEmail,
  error,
  onClose,
  onSend,
  onEditRecipient,
  editableMessage = false,
  message = "",
  onMessageChange,
}) {
  useEffect(() => {
    if (!editableMessage) return undefined;
    const root = document.documentElement;
    const body = document.body;
    const previousRootOverflow = root.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    root.style.overflow = "hidden";
    body.style.overflow = "hidden";
    return () => {
      root.style.overflow = previousRootOverflow;
      body.style.overflow = previousBodyOverflow;
    };
  }, [editableMessage]);

  return (
    <div
      className="quotation-dialog-scrim"
      data-clickable="true"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        className="quotation-send-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        data-clickable={false}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="quotation-dialog-header">
          <h3 id={`${id}-title`}>
            {editableMessage ? "Follow up quote" : "Send quote"}
          </h3>
          <button
            type="button"
            className="quotation-dialog-close-btn"
            disabled={busy}
            onClick={onClose}
          >
            <LuX />
          </button>
        </div>

        <div className="quotation-dialog-body">
          <QuotationRecipientField
            id={id}
            primaryEmail={recipient}
            disabled={busy}
            primaryError={recipientError}
            additionalError={additionalRecipientsError}
            onChange={onRecipientsChange}
            onError={onRecipientsError}
          />
          {recipientError && onEditRecipient && (
            <button
              type="button"
              className="quotation-dialog-cancel-btn"
              onClick={onEditRecipient}
            >
              Update recipient email in quote
            </button>
          )}

          <div className="quotation-dialog-field">
            <label>Subject</label>
            <input
              value={subject}
              onChange={(e) => onSubjectChange(e.target.value)}
              placeholder="Email subject"
            />
          </div>

          <div className="quotation-dialog-preview-section">
            <div className="quotation-dialog-preview-heading">
              <LuEye />
              <span>{editableMessage ? "Email message" : "Email preview"}</span>
            </div>
            <div className="quotation-dialog-email-card">
              {editableMessage ? (
                <>
                  <p className="quotation-email-greeting">
                    Hello {values.name || values.contactName || "Customer"},
                  </p>
                  <textarea
                    id={`${id}-message`}
                    className="quotation-followup-message"
                    aria-label="Email message"
                    rows={7}
                    value={message}
                    disabled={busy}
                    onChange={(event) => onMessageChange(event.target.value)}
                  />
                  <p className="quotation-email-validity">
                    Valid for{" "}
                    {String(values.validUpto || "14 days").replace(
                      /\bDays?\b/i,
                      "days"
                    )}{" "}
                    from sending.
                  </p>
                </>
              ) : (
                <>
                  <p className="quotation-email-greeting">
                    Hello {values.name || values.contactName || "Customer"},
                  </p>
                  <p className="quotation-email-text">
                    Please find quote {values.quotationNumber || "QUO"} for $
                    {formatCurrencyAUD(parseFloat(values.finalAmount) || 0)}{" "}
                    {currencyCode || "AUD"} from {organisation}. You can review
                    the details and approve your quote online.
                  </p>
                  <p className="quotation-email-validity">
                    Valid for{" "}
                    {String(values.validUpto || "14 days").replace(
                      /\bDays?\b/i,
                      "days"
                    )}{" "}
                    from sending.
                  </p>
                </>
              )}
              <span className="quotation-email-preview-cta">
                Review &amp; approve
              </span>
              {values.quotationStatus === "Approved" && (
                <div className="quotation-approval-success" role="status">
                  <LuCheck aria-hidden="true" />
                  Approved
                </div>
              )}
              <div className="quotation-email-signoff">
                <p>Thanks,</p>
                <p>{organisation}</p>
              </div>
            </div>
          </div>

          <div className="quotation-dialog-delivery-options">
            <label className="quotation-dialog-checkbox">
              <input
                type="checkbox"
                checked={attachPdf}
                onChange={(e) => onAttachPdfChange(e.target.checked)}
              />
              <span>Attach quote PDF</span>
            </label>
            <label className="quotation-dialog-checkbox">
              <input
                type="checkbox"
                checked={sendCopy}
                onChange={(e) => onSendCopyChange(e.target.checked)}
              />
              <span>Send me a copy</span>
            </label>
          </div>
          {error && (
            <p className="quotation-field-error" role="alert">
              {error}
            </p>
          )}
        </div>

        <div className="quotation-dialog-footer">
          <button
            type="button"
            className="quotation-dialog-cancel-btn"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="quotation-dialog-send-btn"
            disabled={busy || Boolean(recipientError)}
            onClick={onSend}
          >
            <LuSend />
            <span>
              {sendingEmail
                ? "Sending..."
                : editableMessage
                ? "Send follow up"
                : "Send quote"}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

QuotationSendDialog.propTypes = {
  id: PropTypes.string.isRequired,
  values: PropTypes.object.isRequired,
  organisation: PropTypes.string.isRequired,
  currencyCode: PropTypes.string,
  recipient: PropTypes.string.isRequired,
  recipientError: PropTypes.string,
  additionalRecipientsError: PropTypes.string,
  onRecipientsChange: PropTypes.func.isRequired,
  onRecipientsError: PropTypes.func.isRequired,
  subject: PropTypes.string.isRequired,
  onSubjectChange: PropTypes.func.isRequired,
  attachPdf: PropTypes.bool.isRequired,
  onAttachPdfChange: PropTypes.func.isRequired,
  sendCopy: PropTypes.bool.isRequired,
  onSendCopyChange: PropTypes.func.isRequired,
  busy: PropTypes.bool.isRequired,
  sendingEmail: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onEditRecipient: PropTypes.func,
  editableMessage: PropTypes.bool,
  message: PropTypes.string,
  onMessageChange: PropTypes.func,
};
